import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { KeyedMutator } from 'swr';
import { toast } from '@/components';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import type { Person } from '@/types/person';
import {
    getPersonMarkAsUnknownUpdate,
    markingAsUnknownRemovesTags,
} from '@/utils/person';
import { revalidateByCacheKeyPrefix } from '@/utils/swr';
import type { DirectoryItemAction } from '@/features/DirectoryItemPage/types';
import { MergePersonModal } from '@/features/MergeIdentity/person/MergePersonModal';
import { DeletePersonConfirmationModal } from '@/features/PersonGrid/components/DeletePersonConfirmationModal';
import { MarkPersonAsUnknownConfirmationModal } from '@/features/PersonGrid/components/MarkPersonAsUnknownConfirmationModal';
import { UpdatePersonModal } from '@/features/PersonGrid/components/UpdatePersonModal';
import { usePersonUpdate } from '@/features/PersonGrid/hooks/usePersonUpdate';
import {
    getIdentityActionDescriptors,
    type IdentityActionKind,
} from './identityActionDescriptors';
import type { IdentityActionsResult } from './types';

interface UsePersonIdentityActionsOptions {
    person: Person | undefined;
    includeDelete: boolean;
    mutateDetail: KeyedMutator<Person> | undefined;
    onChanged: () => void;
    onDeleted: () => void;
    onMerged: (targetPersonId: string) => void;
    onSourceStale: () => void;
}

export function usePersonIdentityActions({
    person,
    includeDelete,
    mutateDetail,
    onChanged,
    onDeleted,
    onMerged,
    onSourceStale,
}: UsePersonIdentityActionsOptions): IdentityActionsResult {
    const { t } = useTranslation(I18nNamespace.DIRECTORY);
    const [openModal, setOpenModal] = useState<IdentityActionKind | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { isUpdating, updatePerson } = usePersonUpdate({ mutateDetail });

    if (!person) {
        return { actions: [], modals: null, openAction: async () => {} };
    }

    const editPerson = async (
        personId: string,
        payload: Parameters<typeof updatePerson>[1],
    ): Promise<boolean> => {
        try {
            await updatePerson(personId, payload);
            toast.success(t('persons.editModal.success'));
            onChanged();
            return true;
        } catch {
            toast.error(t('persons.editModal.error'));
            return false;
        }
    };

    const markAsUnknown = async () => {
        const { payload, optimisticPatch } = getPersonMarkAsUnknownUpdate();
        mutateDetail?.(
            { ...person, ...optimisticPatch },
            { revalidate: false },
        );
        try {
            await updatePerson(person.id, payload);
            toast.success(t('persons.markAsUnknown.success'));
            onChanged();
        } catch {
            mutateDetail?.(person, { revalidate: false });
            toast.error(t('persons.markAsUnknown.error'));
        }
    };

    const deletePerson = async () => {
        setIsDeleting(true);
        try {
            await EngineService.deletePerson(person.id);
            void revalidateByCacheKeyPrefix(CACHE_KEYS.ALL_PERSONS);
            void revalidateByCacheKeyPrefix(CACHE_KEYS.ALL_PERSONS_STATS);
            toast.success(t('persons.deleteSuccess', { count: 1 }));
            setOpenModal(null);
            onChanged();
            onDeleted();
        } catch {
            toast.error(t('persons.deleteError'));
        } finally {
            setIsDeleting(false);
        }
    };

    const openAction = async (kind: IdentityActionKind) => {
        if (kind === 'markAsUnknown') {
            if (markingAsUnknownRemovesTags(person)) {
                setOpenModal('markAsUnknown');
                return;
            }
            await markAsUnknown();
            return;
        }
        setOpenModal(kind === 'markAsRecognized' ? 'edit' : kind);
    };

    const actions = getIdentityActionDescriptors({
        entityKind: 'person',
        known: person.known,
        includeDelete,
        t,
    }).map((descriptor) => ({
        icon: descriptor.icon,
        label: descriptor.label,
        onClick: () => void openAction(descriptor.kind),
    }));

    const modals = (
        <>
            <UpdatePersonModal
                isOpen={openModal === 'edit'}
                onClose={() => setOpenModal(null)}
                isSubmitting={isUpdating}
                mode={person.known ? 'edit' : 'recognize'}
                person={person}
                onUpdate={editPerson}
            />
            <MergePersonModal
                isOpen={openModal === 'merge'}
                onClose={() => setOpenModal(null)}
                variant="merge"
                sourcePerson={person}
                onSuccess={(result) => {
                    void revalidateByCacheKeyPrefix(CACHE_KEYS.ALL_PERSONS);
                    void revalidateByCacheKeyPrefix(
                        CACHE_KEYS.ALL_PERSONS_STATS,
                    );
                    onChanged();
                    onMerged(result.targetPerson.id);
                }}
                onSourceStale={() => {
                    void revalidateByCacheKeyPrefix(CACHE_KEYS.ALL_PERSONS);
                    void revalidateByCacheKeyPrefix(
                        CACHE_KEYS.ALL_PERSONS_STATS,
                    );
                    onSourceStale();
                }}
            />
            <MarkPersonAsUnknownConfirmationModal
                isOpen={openModal === 'markAsUnknown'}
                tags={person.tags}
                isSubmitting={isUpdating}
                onConfirm={() => {
                    setOpenModal(null);
                    void markAsUnknown();
                }}
                onCancel={() => setOpenModal(null)}
            />
            <DeletePersonConfirmationModal
                isOpen={openModal === 'delete'}
                deleteInProgress={isDeleting}
                onConfirm={deletePerson}
                onCancel={() => setOpenModal(null)}
            />
        </>
    );

    return { actions, modals, openAction };
}
